import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Activity, ArrowRight, CheckCircle2, Clock } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "pickup", label: "Pickups (PR)" },
  { id: "delivery", label: "Deliveries (DR)" },
  { id: "in_flight", label: "In flight" },
  { id: "complete", label: "Complete" },
];

// Compact horizontal-timeline view of every request in flight or recently completed.
// One row per request: created date → dispatched → received date (or "in flight" for pending).
export default function DispatchTimeline() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/requests");
        setRows(data || []);
      } catch {
        setRows([]);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const list = rows.filter((r) => {
      if (filter === "pickup") return r.request_type === "pickup";
      if (filter === "delivery") return r.request_type === "delivery";
      if (filter === "in_flight") return !r.receival?.received;
      if (filter === "complete") return Boolean(r.receival?.received);
      return true;
    });
    return list.slice(0, 12);
  }, [rows, filter]);

  const counts = useMemo(() => {
    if (!rows) return {};
    return {
      all: rows.length,
      pickup: rows.filter((r) => r.request_type === "pickup").length,
      delivery: rows.filter((r) => r.request_type === "delivery").length,
      in_flight: rows.filter((r) => !r.receival?.received).length,
      complete: rows.filter((r) => Boolean(r.receival?.received)).length,
    };
  }, [rows]);

  const today = new Date();

  // Figure out the time window so we can plot every request on the same scale.
  const { minMs, maxMs } = useMemo(() => {
    if (!filtered || filtered.length === 0) return { minMs: today.getTime() - 14 * 86400000, maxMs: today.getTime() };
    let lo = Infinity, hi = -Infinity;
    for (const r of filtered) {
      const c = r.created_at ? new Date(r.created_at).getTime() : null;
      const done = r.receival?.received_at ? new Date(r.receival.received_at).getTime()
                : r.receival?.received_date ? new Date(r.receival.received_date).getTime()
                : null;
      if (c) { lo = Math.min(lo, c); hi = Math.max(hi, c); }
      if (done) hi = Math.max(hi, done);
    }
    hi = Math.max(hi, today.getTime());
    if (!isFinite(lo)) lo = today.getTime() - 14 * 86400000;
    return { minMs: lo - 86400000, maxMs: hi + 86400000 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  const pct = (ms) => {
    if (!ms) return null;
    const w = maxMs - minMs || 1;
    return Math.max(0, Math.min(100, ((ms - minMs) / w) * 100));
  };

  const daysBetween = (a, b) => Math.max(0, Math.round((b - a) / 86400000));

  if (!rows) return null;

  return (
    <section data-testid="dispatch-timeline" className="border-2 border-border bg-card">
      <div className="px-5 py-4 border-b-2 border-border flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="border-2 border-orange-600 bg-orange-600 text-white p-1.5">
            <Activity className="h-3.5 w-3.5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Dispatch Tracker</div>
            <h2 className="font-heading text-lg sm:text-xl font-black tracking-tight">Request timeline</h2>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-orange-500 inline-block" /> Created</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-sky-500 inline-block" /> Dispatched</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-600 inline-block" /> Received</span>
        </div>
      </div>

      {rows.length === 0 && (
        <div data-testid="dispatch-timeline-empty" className="p-6 text-sm text-muted-foreground">
          No requests yet — log your first pickup or delivery to see it appear here.
        </div>
      )}

      {rows.length > 0 && (
        <div className="px-5 py-3 border-b-2 border-border flex items-center gap-1.5 flex-wrap overflow-x-auto">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            const count = counts[f.id] ?? 0;
            return (
              <button
                key={f.id}
                type="button"
                data-testid={`timeline-filter-${f.id}${active ? "-active" : ""}`}
                onClick={() => setFilter(f.id)}
                className={`h-8 px-3 text-[11px] uppercase tracking-[0.15em] font-bold border-2 transition-colors whitespace-nowrap ${
                  active
                    ? "border-orange-600 bg-orange-600 text-white"
                    : "border-border bg-card hover:border-orange-600"
                }`}
              >
                {f.label}
                <span className={`ml-2 font-mono ${active ? "text-white/80" : "text-muted-foreground"}`}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {rows.length > 0 && filtered.length === 0 && (
        <div data-testid="dispatch-timeline-filter-empty" className="p-6 text-sm text-muted-foreground">
          No requests match this filter.
        </div>
      )}

      <div className="divide-y divide-border">
        {filtered.map((r) => {
          const created = r.created_at ? new Date(r.created_at) : null;
          const receivedIso = r.receival?.received_at || r.receival?.received_date;
          const received = receivedIso ? new Date(receivedIso) : null;
          const dispatchedEntry = (r.timeline || []).find((t) => t.status === "dispatched");
          const dispatched = dispatchedEntry?.at ? new Date(dispatchedEntry.at) : null;

          const startPct = created ? pct(created.getTime()) : 0;
          const endPct = received ? pct(received.getTime()) : pct(today.getTime());
          const dispatchPct = dispatched ? pct(dispatched.getTime()) : null;

          const days = created ? daysBetween(created.getTime(), (received || today).getTime()) : 0;
          const inFlight = !received;

          const fromCompany = r.pickup?.company || "—";
          const toCompany = r.delivery?.company || "—";

          return (
            <Link
              to={`/requests/${r.request_id}`}
              key={r.request_id}
              data-testid={`timeline-row-${r.reference_number}`}
              className="block px-5 py-3 hover:bg-accent/40 transition-colors"
            >
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                <div className="sm:col-span-3 min-w-0">
                  <div className="font-mono text-xs font-bold truncate">{r.reference_number}</div>
                  <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
                    {fromCompany} <ArrowRight className="h-3 w-3 shrink-0" /> {toCompany}
                  </div>
                </div>

                <div className="sm:col-span-7 relative h-6">
                  {/* baseline */}
                  <div className="absolute inset-y-1/2 left-0 right-0 h-0.5 -translate-y-1/2 bg-border" />
                  {/* progress bar */}
                  {startPct != null && endPct != null && (
                    <div
                      className={`absolute inset-y-1/2 h-1 -translate-y-1/2 ${received ? "bg-emerald-600" : "bg-orange-500"}`}
                      style={{ left: `${Math.min(startPct, endPct)}%`, width: `${Math.abs(endPct - startPct)}%` }}
                      title={received ? `${days} days end-to-end` : `${days} days in flight`}
                    />
                  )}
                  {/* Created dot */}
                  {startPct != null && (
                    <span
                      className="absolute top-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-orange-500 border-2 border-white dark:border-black shadow"
                      style={{ left: `calc(${startPct}% - 5px)` }}
                      title={created ? `Created ${created.toLocaleDateString()}` : ""}
                    />
                  )}
                  {/* Dispatched dot */}
                  {dispatchPct != null && (
                    <span
                      className="absolute top-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-sky-500 border-2 border-white dark:border-black shadow"
                      style={{ left: `calc(${dispatchPct}% - 5px)` }}
                      title={dispatched ? `Dispatched ${dispatched.toLocaleDateString()}` : ""}
                    />
                  )}
                  {/* Received dot / today marker */}
                  <span
                    className={`absolute top-1/2 -translate-y-1/2 h-3 w-3 rounded-full border-2 border-white dark:border-black shadow ${received ? "bg-emerald-600" : "bg-orange-500 ring-2 ring-orange-200 dark:ring-orange-900"}`}
                    style={{ left: `calc(${endPct}% - 6px)` }}
                    title={received ? `Received ${received.toLocaleDateString()}` : `In flight — ${days} days`}
                  />
                </div>

                <div className="sm:col-span-2 text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center justify-end gap-1">
                    {inFlight ? <Clock className="h-3 w-3 text-orange-600" /> :
                     <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                    {inFlight ? "In flight" : "Complete"}
                  </div>
                  <div className="font-mono text-xs font-bold">{days} day{days === 1 ? "" : "s"}</div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
